import { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input/input';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import {
  useClickstreamCallback,
  useModalDisplayedEvent
} from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { useUpsertLead, useVisit } from '@zenbusiness/application-commons-visit';
import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';

import AuthModal from './AuthModal';
import * as Styled from './CreateAccountModal.style';
import PasswordField from '../PasswordField';
import PasswordValidationBox from './components/PasswordValidationBox';
import { AUTH_VALIDATIONS } from './constants/validations';

// GraphQL mutation for account creation
const CREATE_ACCOUNT_MUTATION = `
  mutation CreateUserAccount($input: CreateUserAccountInput!) {
    createUserAccount(input: $input) {
      ok
      accountUuid
      error {
        message
        code
        details
      }
      meta {
        statusCode
        message
      }
    }
  }
`;

/**
 * Create Account Modal
 *
 * Allows users to create a new account with email, password, name.
 * After successful account creation, automatically logs the user in.
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} [props.onSuccess] - Callback when account is created successfully
 * @param {Function} [props.onSwitchToLogin] - Callback to switch to login modal
 * @returns {object} The CreateAccountModal component
 */
function CreateAccountModal({ isOpen, onClose, onSuccess, onSwitchToLogin }) {
  const [globalError, setGlobalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createAccountMutation] = useMutation(CREATE_ACCOUNT_MUTATION);
  const { loginWithPassword } = useAuthentication();
  const captureException = useCaptureException();

  // Feature flag for lead creation
  const enableLeadCreation = useExperimentFlag('enable-velo-lead-creation', false);

  // Lead creation hooks
  const { upsert: upsertLead } = useUpsertLead();
  const { visitUuid } = useVisit();

  // Analytics tracking
  const trackLeadCreated = useClickstreamCallback('leadCreated');
  const trackLeadUpdated = useClickstreamCallback('leadUpdated');
  const trackUserIdentified = useClickstreamCallback('userIdentified');
  const trackAccountCreated = useClickstreamCallback('accountCreated');

  // Track modal display
  useModalDisplayedEvent({
    open: isOpen,
    elementName: 'create_account_modal'
  });

  // Initialize form
  const formMethods = useForm({
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: ''
    },
    mode: 'onBlur' // Validate on blur for better UX
  });

  const { handleSubmit, register, watch, formState } = formMethods;
  const { errors } = formState;

  // Watch password value for real-time validation display
  const passwordValue = watch('password');

  /**
   * Handle form submission
   * Flow: Create lead first -> Create account with leadUuid -> Auto-login
   */
  const onSubmit = useCallback(
    async (data) => {
      setIsSubmitting(true);
      setGlobalError(null);

      let leadUuid = null;

      // Step 1: Create lead FIRST (before account creation) - only if feature flag is enabled
      if (enableLeadCreation) {
        // Lead creation failure should not block account creation
        // Note: useUpsertLead has noThrow: true by default, so errors are returned, not thrown
        const leadInput = {
          contact: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone
          },
          tracking: {
            source: 'velo'
          },
          visitUuid
        };

        const { lead, error: leadError } = await upsertLead(leadInput);

        if (leadError) {
          console.error('[CreateAccountModal] Lead creation error:', leadError);
          // Log lead creation error but don't block registration
          captureException(leadError, {
            level: 'warning',
            context: { action: 'create_lead_before_registration' }
          });
        } else if (lead?.uuid) {
          leadUuid = lead.uuid;

          // Identify user for downstream analytics
          trackUserIdentified({
            userId_: lead.uuid,
            leadUuid: lead.uuid,
            firstname: data.firstName,
            lastname: data.lastName,
            email: data.email,
            phone: data.phone
          });

          // Track successful lead creation or update
          if (lead.isNew) {
            trackLeadCreated({ dynamicProperties: { leadUuid: lead.uuid } });
          } else {
            trackLeadUpdated({ dynamicProperties: { leadUuid: lead.uuid } });
          }
        }
      }

      try {
        // Step 2: Create account via GraphQL mutation (with leadUuid if available)
        const result = await createAccountMutation({
          variables: {
            input: {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              password: data.password,
              phone: data.phone,
              ...(leadUuid ? { leadUuid } : {})
            }
          }
        });

        const { ok, accountUuid, error: apiError } = result.data.createUserAccount;

        if (!ok || !accountUuid) {
          // Handle API error
          const errorMessage = apiError?.message || 'Failed to create account';
          const errorCode = apiError?.code;

          captureException(new Error(errorMessage), {
            level: 'error',
            context: {
              action: 'create_account',
              errorCode,
              statusCode: result.data.createUserAccount.meta?.statusCode
            }
          });

          if (errorCode === 'ACCOUNT_EXISTS') {
            setGlobalError('An account with this email already exists. Please login to continue.');
          } else {
            console.error(errorMessage);
            setGlobalError('Unable to create account. Please try again.');
          }

          setIsSubmitting(false);
          return;
        }

        // Track successful account creation
        trackAccountCreated();

        // Determine redirect URL - avoid returning to anonymous threads
        // If user is on a thread page, redirect to chat entry instead
        // (anonymous threads can't be accessed by authenticated users)
        const isOnThreadPage = window.location.pathname.includes('/thread/');
        const targetRedirect = isOnThreadPage
          ? '/guide/chat'
          : window.location.pathname + window.location.search;

        // Step 3: Account created successfully! Auto-login
        try {
          await loginWithPassword({
            email: data.email,
            password: data.password,
            redirectTo: targetRedirect
          });

          // Success! Call onSuccess callback
          if (onSuccess) {
            onSuccess({ accountUuid, email: data.email });
          }

          // Close modal
          onClose();
        } catch (loginError) {
          // Account created but login failed
          captureException(loginError, {
            level: 'warning',
            context: { action: 'auto_login_after_create_account' }
          });
        }
      } catch (error) {
        captureException(error, {
          level: 'error',
          context: { action: 'create_account_mutation' }
        });

        // Never expose raw error.message - it could contain internal details
        // The actual error is logged to Sentry for debugging
        setGlobalError('An unexpected error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      createAccountMutation,
      loginWithPassword,
      onSuccess,
      onClose,
      captureException,
      enableLeadCreation,
      upsertLead,
      visitUuid,
      trackLeadCreated,
      trackLeadUpdated,
      trackUserIdentified,
      trackAccountCreated
    ]
  );

  /**
   * Handle form submission errors
   */
  const onSubmitError = useCallback((formErrors) => {
    console.error('Form validation errors:', formErrors);
    setGlobalError('Please fix the errors above and try again.');
  }, []);

  /**
   * Handle switch to login
   */
  const handleSwitchToLogin = useCallback(
    (e) => {
      e.preventDefault();
      if (onSwitchToLogin) {
        onSwitchToLogin();
      }
    },
    [onSwitchToLogin]
  );

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <AuthModal isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <Styled.FormRoot
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
        >
          <Styled.FormStack spacing={3}>
            {/* Global Error */}
            {globalError && (
              <Box pb={2}>
                <Text color="red" size="sm">
                  {globalError}
                </Text>
              </Box>
            )}
            <Styled.TitleContainer>
              <Styled.Title size="lg" weight="bold">
                Create your Velo account
              </Styled.Title>
              <Styled.Subtitle>
                Already have an account?{' '}
                <Styled.SwitchLink disabled={isSubmitting} onClick={handleSwitchToLogin}>
                  Log in
                </Styled.SwitchLink>
              </Styled.Subtitle>
            </Styled.TitleContainer>
            {/* Name Fields - Stacked */}
            <Styled.FieldStack>
              <Stack direction="row" gap={2}>
                <TextField
                  {...register('firstName', AUTH_VALIDATIONS.firstName)}
                  autoComplete="given-name"
                  disabled={isSubmitting}
                  error={Boolean(errors.firstName)}
                  fullWidth
                  helperText={errors.firstName?.message}
                  label="First Name"
                  required
                />
                <TextField
                  {...register('lastName', AUTH_VALIDATIONS.lastName)}
                  autoComplete="family-name"
                  disabled={isSubmitting}
                  error={Boolean(errors.lastName)}
                  fullWidth
                  helperText={errors.lastName?.message}
                  label="Last Name"
                  required
                />
              </Stack>
              {/* Email Field */}
              <TextField
                {...register('email', AUTH_VALIDATIONS.email)}
                autoComplete="email"
                disabled={isSubmitting}
                error={Boolean(errors.email)}
                fullWidth
                helperText={errors.email?.message}
                label="Email"
                required
                type="email"
              />
              {/* Phone Field */}
              <Controller
                control={formMethods.control}
                name="phone"
                render={({ field: { onChange, value, onBlur, ref } }) => (
                  <TextField
                    autoComplete="tel"
                    disabled={isSubmitting}
                    error={Boolean(errors.phone)}
                    fullWidth
                    helperText={errors.phone?.message}
                    InputProps={{
                      inputComponent: PhoneInput,
                      inputProps: {
                        country: 'US',
                        autoComplete: 'tel',
                        inputMode: 'tel',
                        'data-testid': 'phone-number-field',
                        onChange,
                        value: value || '',
                        onBlur,
                        ref
                      }
                    }}
                    label="Phone Number"
                    required
                    type="tel"
                  />
                )}
                rules={AUTH_VALIDATIONS.phone}
              />

              {/* Password Field */}
              <PasswordField
                {...register('password', AUTH_VALIDATIONS.newPassword)}
                autoComplete="new-password"
                disabled={isSubmitting}
                error={Boolean(errors.password)}
                fullWidth
                label="Password"
                required
              />
            </Styled.FieldStack>

            {/* Password Validation Box with real-time feedback */}
            <PasswordValidationBox error={errors.password} value={passwordValue} />
            <Styled.TermsText size="sm">
              By selecting Create Account, you agree to our&nbsp;
              <Styled.TermsLink
                href="https://www.zenbusiness.com/terms-of-use"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of Use
              </Styled.TermsLink>
              &nbsp;and acknowledge you have read our&nbsp;
              <Styled.TermsLink
                href="https://www.zenbusiness.com/privacy-policy"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy
              </Styled.TermsLink>
              .
            </Styled.TermsText>

            <Styled.ButtonStack>
              <Styled.SubmitButton
                disabled={hasErrors || isSubmitting}
                loading={isSubmitting}
                type="submit"
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </Styled.SubmitButton>

              <Styled.CancelButton variant="outlined" onClick={onClose}>
                Close
              </Styled.CancelButton>
            </Styled.ButtonStack>
          </Styled.FormStack>
        </Styled.FormRoot>
      </FormProvider>
    </AuthModal>
  );
}

CreateAccountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onSwitchToLogin: PropTypes.func
};

export default CreateAccountModal;
